import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Wand2, Plus, Trash2, Pencil, Loader2, ArrowUpDown } from 'lucide-react';

const NONE = '__none__';
const CONDITION_TYPES = [
  { value: 'customer', label: 'Customer' },
  { value: 'site', label: 'Site' },
  { value: 'equipment_type', label: 'Equipment Type' },
  { value: 'job_type', label: 'Job Type' },
];

export default function TrackingRulesManager({ api, platform, platformName, connectionId, trackingCategories = [] }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);

  // Lookup data
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);

  const [form, setForm] = useState({
    name: '',
    condition_type: 'customer',
    condition_value: '',
    condition_label: '',
    tracking: {},
    priority: 0,
    is_active: true,
  });

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ platform });
      if (connectionId) params.append('connection_id', connectionId);
      const res = await api.get(`/payroll/tracking-rules?${params}`);
      setRules(res.data.rules || []);
    } catch {
      toast.error('Failed to load tracking rules');
    } finally {
      setLoading(false);
    }
  }, [api, platform, connectionId]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  // Load customers and sites for the selectors
  useEffect(() => {
    Promise.all([
      api.get('/customers?status=active&limit=500').catch(() => ({ data: [] })),
      api.get('/sites?limit=500').catch(() => ({ data: [] })),
    ]).then(([custRes, siteRes]) => {
      setCustomers(Array.isArray(custRes.data) ? custRes.data : (custRes.data?.customers || []));
      setSites(Array.isArray(siteRes.data) ? siteRes.data : (siteRes.data?.sites || []));
    });
  }, [api]);

  const openCreate = () => {
    setEditingRule(null);
    setForm({
      name: '', condition_type: 'customer', condition_value: '', condition_label: '',
      tracking: {}, priority: rules.length, is_active: true,
    });
    setShowDialog(true);
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      name: rule.name || '',
      condition_type: rule.condition_type || 'customer',
      condition_value: rule.condition_value || '',
      condition_label: rule.condition_label || '',
      tracking: rule.tracking || {},
      priority: rule.priority || 0,
      is_active: rule.is_active !== false,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Rule name is required'); return; }
    if (!form.condition_value) { toast.error('Select a condition value'); return; }
    setSaving(true);
    try {
      if (editingRule) {
        await api.put(`/payroll/tracking-rules/${editingRule.id}`, form);
        toast.success('Rule updated');
      } else {
        await api.post('/payroll/tracking-rules', { ...form, platform, connection_id: connectionId || null });
        toast.success('Rule created');
      }
      setShowDialog(false);
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleId) => {
    try {
      await api.delete(`/payroll/tracking-rules/${ruleId}`);
      toast.success('Rule deleted');
      fetchRules();
    } catch {
      toast.error('Failed to delete rule');
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await api.put(`/payroll/tracking-rules/${rule.id}`, { is_active: !rule.is_active });
      fetchRules();
    } catch {
      toast.error('Failed to toggle rule');
    }
  };

  // Determine condition value options based on selected type
  const conditionOptions = () => {
    switch (form.condition_type) {
      case 'customer':
        return customers.map(c => ({ value: c.id, label: c.company_name || c.name || c.id }));
      case 'site':
        return sites.map(s => ({ value: s.id, label: s.name || s.id }));
      default:
        return []; // Free text for equipment_type, job_type
    }
  };

  const options = conditionOptions();
  const useFreeText = form.condition_type === 'equipment_type' || form.condition_type === 'job_type';

  // Tracking form per platform
  const renderTrackingForm = () => {
    if (platform === 'xero' && trackingCategories.length > 0) {
      return (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground font-semibold">Xero Tracking Selections</Label>
          {trackingCategories.slice(0, 2).map(cat => {
            const current = (form.tracking?.xero || []).find(t => t.category_id === cat.id);
            return (
              <div key={cat.id} className="grid grid-cols-2 gap-2 items-center">
                <Label className="text-xs">{cat.name}</Label>
                <Select
                  value={current?.option_id || NONE}
                  onValueChange={(v) => {
                    const opt = cat.options.find(o => o.id === v);
                    const existing = (form.tracking?.xero || []).filter(t => t.category_id !== cat.id);
                    if (v !== NONE) {
                      existing.push({
                        category_id: cat.id, category_name: cat.name,
                        option_id: v, option_name: opt?.name || '',
                      });
                    }
                    setForm({ ...form, tracking: { ...form.tracking, xero: existing } });
                  }}
                >
                  <SelectTrigger className="h-8 text-xs" data-testid={`rule-xero-tracking-${cat.id}`}>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {cat.options.filter(o => o.status === 'ACTIVE').map(opt => (
                      <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </div>
      );
    }
    if (platform === 'myob') {
      return (
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground font-semibold">MYOB Job UID</Label>
          <Input
            value={form.tracking?.myob_job_uid || ''}
            onChange={(e) => setForm({ ...form, tracking: { ...form.tracking, myob_job_uid: e.target.value } })}
            placeholder="MYOB Job UID"
            className="h-8 text-xs"
            data-testid="rule-myob-job"
          />
        </div>
      );
    }
    if (platform === 'qbo') {
      return (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground font-semibold">QBO Class</Label>
            <Input
              value={form.tracking?.qbo_class_ref || ''}
              onChange={(e) => setForm({ ...form, tracking: { ...form.tracking, qbo_class_ref: e.target.value } })}
              placeholder="Class ref"
              className="h-8 text-xs"
              data-testid="rule-qbo-class"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground font-semibold">QBO Dept</Label>
            <Input
              value={form.tracking?.qbo_department_ref || ''}
              onChange={(e) => setForm({ ...form, tracking: { ...form.tracking, qbo_department_ref: e.target.value } })}
              placeholder="Dept ref"
              className="h-8 text-xs"
              data-testid="rule-qbo-dept"
            />
          </div>
        </div>
      );
    }
    return <p className="text-xs text-muted-foreground">No tracking options available for {platformName}</p>;
  };

  return (
    <Card className="card-industrial" data-testid="tracking-rules-manager">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Wand2 className="h-5 w-5 text-[#ffd108]" />
          Auto-Tracking Rules
        </CardTitle>
        <CardDescription>
          Automatically assign {platformName} tracking to time blocks based on customer, site, or job type.
          Rules are applied when blocks are auto-generated from dockets.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="text-xs">{rules.length} rule{rules.length !== 1 ? 's' : ''}</Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={openCreate}
            data-testid="create-tracking-rule-btn"
          >
            <Plus className="h-3 w-3 mr-1" /> Add Rule
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : rules.length === 0 ? (
          <div className="text-center py-6 text-xs text-muted-foreground border border-dashed rounded-lg">
            <Wand2 className="h-6 w-6 mx-auto mb-2 opacity-40" />
            No tracking rules configured yet. Create a rule to automatically assign tracking categories to time blocks.
          </div>
        ) : (
          <div className="space-y-2">
            {rules.map((rule, idx) => (
              <div
                key={rule.id}
                className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                  rule.is_active ? 'border-border bg-card' : 'border-border/50 bg-muted/20 opacity-60'
                }`}
                data-testid={`tracking-rule-${idx}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Badge variant="outline" className="text-[10px] shrink-0">
                    <ArrowUpDown className="h-2.5 w-2.5 mr-0.5" />
                    {rule.priority}
                  </Badge>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{rule.name}</div>
                    <div className="text-[10px] text-muted-foreground">
                      When <span className="font-semibold">{rule.condition_type}</span> = <span className="font-semibold">{rule.condition_label || rule.condition_value}</span>
                      {rule.tracking?.xero?.length > 0 && (
                        <> → <span className="text-indigo-400">{rule.tracking.xero.map(t => t.option_name || t.option_id).join(', ')}</span></>
                      )}
                      {rule.tracking?.myob_job_uid && (
                        <> → <span className="text-indigo-400">Job: {rule.tracking.myob_job_uid}</span></>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Switch
                    checked={rule.is_active}
                    onCheckedChange={() => handleToggleActive(rule)}
                    className="h-4 w-7"
                  />
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(rule)}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDelete(rule.id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-[480px]" data-testid="tracking-rule-dialog">
            <DialogHeader>
              <DialogTitle>{editingRule ? 'Edit' : 'Create'} Tracking Rule</DialogTitle>
              <DialogDescription>
                Define a condition that auto-assigns {platformName} tracking to time blocks.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1">
                <Label className="text-xs">Rule Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Acme Corp → Cost Centre A"
                  className="text-sm"
                  data-testid="rule-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Condition Type</Label>
                  <Select value={form.condition_type} onValueChange={(v) => setForm({ ...form, condition_type: v, condition_value: '', condition_label: '' })}>
                    <SelectTrigger className="text-sm" data-testid="rule-condition-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_TYPES.map(ct => (
                        <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Priority (higher = first)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
                    className="text-sm"
                    data-testid="rule-priority-input"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Condition Value</Label>
                {useFreeText ? (
                  <Input
                    value={form.condition_value}
                    onChange={(e) => setForm({ ...form, condition_value: e.target.value, condition_label: e.target.value })}
                    placeholder={`Enter ${form.condition_type} value...`}
                    className="text-sm"
                    data-testid="rule-condition-value-input"
                  />
                ) : (
                  <Select
                    value={form.condition_value || NONE}
                    onValueChange={(v) => {
                      if (v === NONE) {
                        setForm({ ...form, condition_value: '', condition_label: '' });
                      } else {
                        const opt = options.find(o => o.value === v);
                        setForm({ ...form, condition_value: v, condition_label: opt?.label || '' });
                      }
                    }}
                  >
                    <SelectTrigger className="text-sm" data-testid="rule-condition-value">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Select...</SelectItem>
                      {options.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="border-t pt-3">
                <Label className="text-xs font-semibold text-muted-foreground mb-2 block">Apply Tracking</Label>
                {renderTrackingForm()}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]" data-testid="save-tracking-rule-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
